/**
 * UpCart compatibility shim for the native AOV cart drawer (EDMProd).
 *
 * EDMProd's integration differs from MusicCreator's. Nothing in this theme calls
 * UpCart's JS API: a grep for `upcart` returns only comments and settings labels.
 * Instead ~26 sections and blocks fire a bare `fetch(routes.root + 'cart/add.js')`
 * and rely on UpCart intercepting the request to open itself, as the in-section
 * comment says: "UpCart/SlideCart detects the fetch request automatically."
 *
 * So this shim interposes on fetch and XMLHttpRequest, watches for cart mutation
 * routes, and drives <aov-cart-drawer> on success. Every existing add-to-cart call
 * site keeps working untouched, which was the shim's purpose on MusicCreator too.
 *
 * The UpCart JS API surface is kept as well. Nothing calls it today, but it costs
 * nothing and covers anything added later.
 *
 * Stand-down behavior: interception no-ops while a real UpCart is present, and the
 * shim only claims globals that are free. Re-enabling the UpCart app embed rolls
 * everything back with no code changes.
 */
const SHIM_FLAG = '__aovShim';

/** Marks requests the drawer makes itself, so we never react to our own traffic. */
const INTERNAL_HEADER = 'x-aov-internal';

function drawer() {
  return document.querySelector('aov-cart-drawer');
}

function realUpcartPresent() {
  return [window.upcart, window.uCart, window.UpCart].some(
    (candidate) => candidate && !candidate[SHIM_FLAG]
  );
}

function openDrawer() {
  drawer()?.refreshAndOpen?.();
}

function refreshDrawer() {
  drawer()?.refresh?.();
}

/* -------------------------------------------------------------------------- */
/* Cart route matching                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Classify a request URL as a cart mutation.
 * Matches with or without the `.js` suffix and under any locale root, since
 * call sites build URLs from `window.Shopify.routes.root`.
 * @param {string} url
 * @returns {'add'|'change'|null}
 */
function classifyCartUrl(url) {
  let path;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }

  if (/\/cart\/add(\.js)?$/.test(path)) return 'add';
  if (/\/cart\/(change|update|clear)(\.js)?$/.test(path)) return 'change';
  return null;
}

/**
 * React to a cart mutation that some other part of the theme performed.
 * Adds open the drawer, everything else just re-renders it if it is already open.
 * @param {'add'|'change'} kind
 */
function onExternalCartMutation(kind) {
  if (realUpcartPresent()) return;
  if (kind === 'add') openDrawer();
  else refreshDrawer();
}

function hasInternalHeader(headers) {
  if (!headers) return false;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    return headers.has(INTERNAL_HEADER);
  }
  if (Array.isArray(headers)) {
    return headers.some(([key]) => String(key).toLowerCase() === INTERNAL_HEADER);
  }
  return Object.keys(headers).some((key) => key.toLowerCase() === INTERNAL_HEADER);
}

/* -------------------------------------------------------------------------- */
/* fetch interception                                                          */
/* -------------------------------------------------------------------------- */

const nativeFetch = window.fetch?.bind(window);

if (nativeFetch && !window.fetch[SHIM_FLAG]) {
  const patched = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url ?? '';
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET') ?? 'GET').toUpperCase();

    const internal =
      hasInternalHeader(init?.headers) ||
      (input instanceof Request && input.headers?.has?.(INTERNAL_HEADER));

    const kind = method === 'POST' ? classifyCartUrl(url) : null;
    const result = nativeFetch(input, init);

    if (!kind || internal) return result;

    // Never read the body here. Call sites consume it themselves and a cart
    // response body can only be read once.
    return result.then((response) => {
      if (response.ok) onExternalCartMutation(kind);
      return response;
    });
  };

  patched[SHIM_FLAG] = true;
  window.fetch = patched;
}

/* -------------------------------------------------------------------------- */
/* XMLHttpRequest interception                                                 */
/* -------------------------------------------------------------------------- */

const XhrProto = window.XMLHttpRequest?.prototype;

if (XhrProto && !XhrProto.open[SHIM_FLAG]) {
  const nativeOpen = XhrProto.open;
  const nativeSetHeader = XhrProto.setRequestHeader;
  const nativeSend = XhrProto.send;

  const patchedOpen = function (method, url, ...rest) {
    this[SHIM_FLAG] = {
      kind: String(method).toUpperCase() === 'POST' ? classifyCartUrl(url) : null,
      internal: false,
    };
    return nativeOpen.call(this, method, url, ...rest);
  };
  patchedOpen[SHIM_FLAG] = true;
  XhrProto.open = patchedOpen;

  XhrProto.setRequestHeader = function (name, value) {
    if (String(name).toLowerCase() === INTERNAL_HEADER && this[SHIM_FLAG]) {
      this[SHIM_FLAG].internal = true;
    }
    return nativeSetHeader.call(this, name, value);
  };

  XhrProto.send = function (...args) {
    const state = this[SHIM_FLAG];

    if (state?.kind && !state.internal) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onExternalCartMutation(state.kind);
      });
    }

    return nativeSend.apply(this, args);
  };
}

/* -------------------------------------------------------------------------- */
/* UpCart JS API surface (unused on EDMProd today, kept for safety)            */
/* -------------------------------------------------------------------------- */

function makeApi() {
  const api = {
    open: openDrawer,
    show: openDrawer,
    openCart: openDrawer,
    openDrawer: openDrawer,
    toggle: () => drawer()?.toggleDialog?.(),
    close: () => drawer()?.close?.(),
    hide: () => drawer()?.close?.(),
    refresh: refreshDrawer,
    reload: refreshDrawer,
    update: refreshDrawer,
  };
  api[SHIM_FLAG] = true;
  return api;
}

if (!window.upcart) window.upcart = makeApi();
if (!window.uCart) window.uCart = makeApi();
if (!window.UpCart) window.UpCart = makeApi();
if (!window.UC) {
  window.UC = { cart: makeApi() };
  window.UC[SHIM_FLAG] = true;
}

const OPEN_EVENTS = ['upcart:open'];
const REFRESH_EVENTS = ['upcart:refresh', 'upcart:cart-updated', 'upcart:cart:refresh'];

for (const name of OPEN_EVENTS) {
  document.addEventListener(name, () => {
    if (realUpcartPresent()) return;
    openDrawer();
  });
}

for (const name of REFRESH_EVENTS) {
  document.addEventListener(name, () => {
    if (realUpcartPresent()) return;
    refreshDrawer();
  });
}
